import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // SEO and Meta
      seo: {
        title: "JSON Path Finder - Free Online JSONPath Extractor",
        description: "Use JSON Path Finder to paste JSON, inspect nested keys and copy JSONPath expressions instantly. Free online JSON analyzer, formatter and path extractor for developers.",
        keywords: "json path finder, JSONPath finder, json path extractor, JSON analyzer, JSON formatter, JSON validator, API testing tool, nested JSON paths",
        faq: {
          what: {
            question: "What is JSON Path Finder?",
            answer: "JSON Path Finder is a free online tool that analyzes JSON data and extracts JSONPath expressions for nested objects, arrays and values."
          },
          how: {
            question: "How do I find a JSON path?",
            answer: "Paste valid JSON into the editor, open the generated path tree and click any item to copy its JSONPath expression to the clipboard."
          }
        }
      },
      
      // Header
      header: {
        title: "JSON Path Finder",
        subtitle: "JSON Path Finder for fast JSONPath extraction",
        description: "Paste, import and inspect nested JSON structures in a high-contrast JSON Path Finder built for quick reading, filtering and copying JSONPath expressions.",
        json5Support: "JSON5 (comments)"
      },

      nav: {
        documentation: "Documentation",
        schema: "Schema"
      },
      
      // JSON Input
      jsonInput: {
        title: "JSON Input",
        subtitle: "Paste your JSON object below",
        subtitleJson5: "Paste your JSON object below (JSON5 comments supported)",
        placeholder: "Paste your JSON here...",
        formatButton: "Format",
        highlightButton: "Highlight",
        formatTooltip: "Format JSON",
        highlightTooltip: "Toggle syntax highlighting"
      },
      
      // JSON Paths
      jsonPaths: {
        title: "JSON Paths",
        subtitle: "Click to select a path, expand/collapse with arrows",
        noData: "No JSON data to display",
        noDataHint: "Paste valid JSON in the left panel to see paths",
        noMatches: "No matches found",
        noMatchesHint: "Try adjusting your search query or check the regex syntax"
      },
      
      // Search
      search: {
        placeholder: "Search paths (supports regex)...",
        searchingFor: "Searching for:",
        regexMode: "REGEX",
        enableRegex: "Enable regex mode",
        disableRegex: "Disable regex mode"
      },
      
      // Selected Path
      selectedPath: {
        title: "Selected Path:",
        copied: "Path copied to clipboard! Click any path to copy it.",
        highlighted: "Highlighted:",
        line: "Line"
      },
      
      // Errors and Validation
      errors: {
        jsonError: "JSON Error:",
        suggestion: "Suggestion:",
        invalidRegex: "Invalid regex pattern:",
        regexHelp: "Please check your regular expression syntax"
      },
      
      // Toast Messages
      toast: {
        pathCopied: "Path copied to clipboard!"
      },
      
      // Footer
      footer: {
        copyright: "All rights reserved.",
        privacy: "Privacy Policy",
        imprint: "Imprint"
      },
      
      // Tool Features (for SEO)
      features: {
        pathExtraction: "Path Extraction",
        jsonValidation: "JSON Validation", 
        syntaxHighlighting: "Syntax Highlighting",
        searchFilter: "Search & Filter",
        formatting: "Auto Formatting",
        json5Support: "JSON5 Support"
      },

      documentation: {
        eyebrow: "Documentation",
        title: "How to use the JSON Path Finder",
        description: "The JSON Path Finder documentation explains the workflow for finding JSONPath expressions in API responses, configuration files and nested data exports.",
        steps: {
          paste: {
            title: "Paste or import JSON",
            text: "Add valid JSON or JSON5 data to the editor. The tool validates the structure and formats the payload for easier reading."
          },
          browse: {
            title: "Browse the generated tree",
            text: "Objects, arrays and values appear as expandable JSON paths, so you can navigate deeply nested data without manual searching."
          },
          use: {
            title: "Use the JSONPath",
            text: "Select any field to copy its JSONPath expression for API tests, data mapping, monitoring rules or developer documentation."
          }
        }
      },

      schema: {
        eyebrow: "Schema guide",
        title: "Understand objects, arrays and values",
        description: "The schema guide shows how the JSON Path Finder represents common JSON node types and converts them into stable JSONPath syntax.",
        items: {
          object: "an expandable group of named keys",
          array: "a list item addressed by its zero-based index",
          value: "a selectable leaf value in the JSON tree",
          example: "example JSONPath for a nested array value"
        }
      },

      seoContent: {
        eyebrow: "Online JSONPath tool",
        title: "Find JSON paths without manual digging",
        description: "This JSON Path Finder turns complex API responses into a searchable tree, so you can locate fields, understand nested data and reuse the exact JSONPath in tests, integrations and documentation.",
        cards: {
          extract: {
            title: "Extract JSON paths",
            text: "Click any object key, array item or value to copy the matching JSONPath expression instantly."
          },
          validate: {
            title: "Validate and format JSON",
            text: "Catch invalid JSON, format payloads and keep large responses readable while you work."
          },
          use: {
            title: "Built for API work",
            text: "Use the generated paths in API tests, data mapping, monitoring rules and developer documentation."
          }
        }
      }
    }
  },
  de: {
    translation: {
      // SEO and Meta
      seo: {
        title: "JSON Path Finder - Kostenloser Online JSONPath Extraktor",
        description: "Nutzen Sie den JSON Path Finder, um JSON einzufügen, verschachtelte Keys zu prüfen und JSONPath-Ausdrücke sofort zu kopieren. Kostenloser JSON Analyzer und Formatter.",
        keywords: "json path finder, JSONPath finder, json pfad finder, json path extraktor, JSON analyzer, JSON formatter, JSON validator, API testing tool",
        faq: {
          what: {
            question: "Was ist der JSON Path Finder?",
            answer: "Der JSON Path Finder ist ein kostenloses Online-Tool, das JSON-Daten analysiert und JSONPath-Ausdrücke für verschachtelte Objekte, Arrays und Werte extrahiert."
          },
          how: {
            question: "Wie finde ich einen JSON Path?",
            answer: "Fügen Sie gültiges JSON in den Editor ein, öffnen Sie den erzeugten Pfadbaum und klicken Sie auf ein Element, um den JSONPath-Ausdruck zu kopieren."
          }
        }
      },
      
      // Header
      header: {
        title: "JSON Path Finder",
        subtitle: "JSON Path Finder für schnelle JSONPath-Extraktion",
        description: "Fügen Sie JSON ein, importieren und prüfen Sie verschachtelte Strukturen in einem JSON Path Finder, der für schnelles Lesen, Filtern und Kopieren von JSONPath-Ausdrücken gebaut ist.",
        json5Support: "JSON5 (Kommentare)"
      },

      nav: {
        documentation: "Dokumentation",
        schema: "Schema"
      },
      
      // JSON Input
      jsonInput: {
        title: "JSON Eingabe",
        subtitle: "Fügen Sie Ihr JSON-Objekt unten ein",
        subtitleJson5: "Fügen Sie Ihr JSON-Objekt unten ein (JSON5 Kommentare unterstützt)",
        placeholder: "Fügen Sie hier Ihr JSON ein...",
        formatButton: "Formatieren",
        highlightButton: "Hervorheben",
        formatTooltip: "JSON formatieren",
        highlightTooltip: "Syntax-Hervorhebung umschalten"
      },
      
      // JSON Paths
      jsonPaths: {
        title: "JSON Pfade",
        subtitle: "Klicken Sie zum Auswählen eines Pfads, erweitern/reduzieren mit Pfeilen",
        noData: "Keine JSON-Daten anzuzeigen",
        noDataHint: "Fügen Sie gültiges JSON im linken Bereich ein, um Pfade zu sehen",
        noMatches: "Keine Treffer gefunden",
        noMatchesHint: "Versuchen Sie, Ihre Suchanfrage anzupassen oder die Regex-Syntax zu überprüfen"
      },
      
      // Search
      search: {
        placeholder: "Pfade durchsuchen (unterstützt Regex)...",
        searchingFor: "Suche nach:",
        regexMode: "REGEX",
        enableRegex: "Regex-Modus aktivieren",
        disableRegex: "Regex-Modus deaktivieren"
      },
      
      // Selected Path
      selectedPath: {
        title: "Ausgewählter Pfad:",
        copied: "Pfad in die Zwischenablage kopiert! Klicken Sie auf einen beliebigen Pfad zum Kopieren.",
        highlighted: "Hervorgehoben:",
        line: "Zeile"
      },
      
      // Errors and Validation
      errors: {
        jsonError: "JSON Fehler:",
        suggestion: "Vorschlag:",
        invalidRegex: "Ungültiges Regex-Muster:",
        regexHelp: "Bitte überprüfen Sie die Syntax Ihres regulären Ausdrucks"
      },
      
      // Toast Messages
      toast: {
        pathCopied: "Pfad in die Zwischenablage kopiert!"
      },
      
      // Footer
      footer: {
        copyright: "Alle Rechte vorbehalten.",
        privacy: "Datenschutz",
        imprint: "Impressum"
      },
      
      // Tool Features (for SEO)
      features: {
        pathExtraction: "Pfad-Extraktion",
        jsonValidation: "JSON-Validierung",
        syntaxHighlighting: "Syntax-Hervorhebung", 
        searchFilter: "Suchen & Filtern",
        formatting: "Auto-Formatierung",
        json5Support: "JSON5-Unterstützung"
      },

      documentation: {
        eyebrow: "Dokumentation",
        title: "So verwenden Sie den JSON Path Finder",
        description: "Die JSON Path Finder Dokumentation erklärt den Workflow zum Finden von JSONPath-Ausdrücken in API-Responses, Konfigurationsdateien und verschachtelten Datenexporten.",
        steps: {
          paste: {
            title: "JSON einfügen oder importieren",
            text: "Fügen Sie gültige JSON- oder JSON5-Daten in den Editor ein. Das Tool validiert die Struktur und formatiert den Payload für bessere Lesbarkeit."
          },
          browse: {
            title: "Erzeugten Baum durchsuchen",
            text: "Objekte, Arrays und Werte erscheinen als ausklappbare JSON Paths, damit Sie tief verschachtelte Daten ohne manuelle Suche navigieren können."
          },
          use: {
            title: "JSONPath verwenden",
            text: "Wählen Sie ein Feld aus, um den JSONPath-Ausdruck für API-Tests, Daten-Mapping, Monitoring-Regeln oder Entwicklerdokumentation zu nutzen."
          }
        }
      },

      schema: {
        eyebrow: "Schema Guide",
        title: "Objekte, Arrays und Werte verstehen",
        description: "Der Schema Guide zeigt, wie der JSON Path Finder typische JSON-Knotentypen darstellt und in stabile JSONPath-Syntax umwandelt.",
        items: {
          object: "eine ausklappbare Gruppe benannter Keys",
          array: "ein Listeneintrag mit nullbasiertem Index",
          value: "ein auswählbarer Endwert im JSON-Baum",
          example: "Beispiel-JSONPath für einen verschachtelten Array-Wert"
        }
      },

      seoContent: {
        eyebrow: "Online JSONPath Tool",
        title: "JSON Paths finden, ohne manuell zu suchen",
        description: "Dieser JSON Path Finder verwandelt komplexe API-Antworten in einen durchsuchbaren Baum, damit Sie Felder finden, verschachtelte Daten verstehen und den exakten JSONPath in Tests, Integrationen und Dokumentation wiederverwenden können.",
        cards: {
          extract: {
            title: "JSON Paths extrahieren",
            text: "Klicken Sie auf jeden Object-Key, Array-Eintrag oder Wert, um den passenden JSONPath-Ausdruck sofort zu kopieren."
          },
          validate: {
            title: "JSON validieren und formatieren",
            text: "Erkennen Sie ungültiges JSON, formatieren Sie Payloads und halten Sie große Responses während der Arbeit lesbar."
          },
          use: {
            title: "Für API-Arbeit gebaut",
            text: "Nutzen Sie erzeugte Paths in API-Tests, Daten-Mappings, Monitoring-Regeln und Entwicklerdokumentation."
          }
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lang',
      caches: ['localStorage']
    },

    interpolation: {
      escapeValue: false
    }
  });

export default i18n;